import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { SERVICE_OPTIONS, SITE } from "@/content/site";
import { QualificationQuestion, formatReason } from "@/components/QualificationQuestion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-navy placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-pool focus:ring-4 focus:ring-cyan-500/15";
const labelCls = "block text-sm font-semibold text-navy mb-1.5";

export const LeadQuoteForm = ({ dark = false }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", service: "", message: "" });
  const [reason, setReason] = useState({ choice: "", otherText: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, reason: formatReason(reason) }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      toast.success("Quote request sent — we'll be in touch shortly.");
    } catch {
      setStatus("idle");
      toast.error(`Something went wrong. Please call us at ${SITE.phone}.`);
    }
  };

  return (
    <div
      data-testid="lead-quote-form"
      className={`relative rounded-2xl p-6 sm:p-8 lg:p-10 ${dark ? "bg-white/[0.06] backdrop-blur-md ring-1 ring-white/15" : "bg-white border border-slate-200/80 shadow-2xl shadow-navy/10"}`}
    >
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            data-testid="lead-form-success"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="py-14 text-center"
          >
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.15 }}
              className="mx-auto w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center"
            >
              <CheckCircle2 className="w-9 h-9 text-emerald-500" />
            </motion.span>
            <h3 className={`mt-6 font-display text-2xl font-bold tracking-tight ${dark ? "text-white" : "text-navy"}`}>
              Request received
            </h3>
            <p className={`mt-3 text-sm sm:text-base max-w-sm mx-auto ${dark ? "text-slate-300" : "text-slate-500"}`}>
              Thanks for reaching out. We&apos;ll review your pool&apos;s needs and get back to you
              shortly. Need us sooner? Call{" "}
              <a href={SITE.phoneHref} data-testid="lead-success-phone-link" className="font-semibold text-pool hover:text-ocean">
                {SITE.phone}
              </a>
              .
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -12 }}
            onSubmit={submit}
            className="space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="lead-name" className={dark ? `${labelCls} text-white` : labelCls}>Name *</label>
                <input id="lead-name" data-testid="lead-form-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} autoComplete="name" />
              </div>
              <div>
                <label htmlFor="lead-phone" className={dark ? `${labelCls} text-white` : labelCls}>Phone *</label>
                <input id="lead-phone" data-testid="lead-form-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(727) 555-0123" className={inputCls} autoComplete="tel" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="lead-email" className={dark ? `${labelCls} text-white` : labelCls}>Email *</label>
                <input id="lead-email" data-testid="lead-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} autoComplete="email" />
              </div>
              <div>
                <label htmlFor="lead-address" className={dark ? `${labelCls} text-white` : labelCls}>Property Address or ZIP *</label>
                <input id="lead-address" data-testid="lead-form-address-input" required value={form.address} onChange={set("address")} placeholder="Street address or ZIP code" className={inputCls} autoComplete="street-address" />
              </div>
            </div>
            <div>
              <label htmlFor="lead-service" className={dark ? `${labelCls} text-white` : labelCls}>Type of Service Needed *</label>
              <select id="lead-service" data-testid="lead-form-service-select" required value={form.service} onChange={set("service")} className={`${inputCls} ${form.service ? "" : "text-slate-400"}`}>
                <option value="" disabled>Select a service…</option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lead-message" className={dark ? `${labelCls} text-white` : labelCls}>
                Message <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <textarea id="lead-message" data-testid="lead-form-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your pool — size, screen enclosure, saltwater, anything that helps." className={`${inputCls} resize-none`} />
            </div>
            <QualificationQuestion value={reason} onChange={setReason} testidPrefix="lead-form" />
            <button
              type="submit"
              data-testid="lead-form-submit-button"
              disabled={status === "sending"}
              className="group w-full inline-flex items-center justify-center gap-2.5 bg-aqua text-navy font-bold px-8 py-4 rounded-full text-base transition-all duration-300 hover:bg-sky2 hover:text-navy hover:shadow-xl hover:shadow-cyan-500/25 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {status === "sending" ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</>
              ) : (
                <>Request Pool Service <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" /></>
              )}
            </button>
            <p className={`text-xs text-center ${dark ? "text-slate-400" : "text-slate-400"}`}>
              Serving all of Pinellas County, FL. No spam — ever.
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
